import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field, FieldDescription, FieldGroup, FieldSeparator } from '@/components/ui/field';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/contexts/auth-context';
import { cn } from '@/lib/utils';
import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';

export function LoginForm({ className, ...props }: React.ComponentProps<'div'>) {
  const { signInWithGoogle, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSignIn = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    try {
      setError(null);
      await signInWithGoogle();
    } catch (err: any) {
      setError(err.message || 'Failed to sign in. Please try again.');
    }
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <div className="flex justify-center">
        <img
          src="/img/emberdesk-1024-full-tw.png"
          alt="EmberDesk logo"
          width={96}
          height={96}
          className="h-20 w-20 md:h-24 md:w-24 object-contain"
        />
      </div>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          <div className="bg-muted relative hidden md:block aspect-square">
            <img
              src="/img/firestore-auth.png"
              alt="emberdesk"
              className="h-full w-full object-cover"
              onError={(e) => {
                console.error('Failed to load image:', e);
              }}
            />
          </div>
          <div className="flex h-full flex-col p-6 md:p-8">
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">Welcome back</h1>
                <p className="text-muted-foreground text-balance">
                  Login to your emberdesk account
                </p>
              </div>

              {error && (
                <Alert variant="destructive" role="alert" aria-live="polite">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
                Continue with
              </FieldSeparator>

              <Field>
                <Button
                  variant="default"
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Spinner size="sm" />
                      Signing in...
                    </span>
                  ) : (
                    <>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="h-4 w-4"
                      >
                        <path
                          d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                          fill="currentColor"
                        />
                      </svg>
                      <span>Login with Google</span>
                    </>
                  )}
                </Button>
              </Field>

              <FieldDescription className="text-center">
                Don&apos;t have an account? Sign in with Google to create one.
              </FieldDescription>
            </FieldGroup>

            <div className="mt-auto flex items-start justify-center gap-2 pt-6">
              <ShieldCheck className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
              <a
                href="https://emberdesk.app/blog/3384821"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary text-muted-foreground text-sm hover:text-foreground hover:underline underline-offset-2"
              >
                See how emberdesk handles your Firestore credentials
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      <FieldDescription className="px-6 text-center">
        By clicking continue, you agree to our{' '}
        <a
          href="https://emberdesk.app/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline underline-offset-2 cursor-pointer"
        >
          Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://emberdesk.app/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline underline-offset-2 cursor-pointer"
        >
          Privacy Policy
        </a>
        .
      </FieldDescription>
    </div>
  );
}
